import type {
  AdminAccountTypeChangeDTO,
  AdminQuotaAddExtraDTO,
  AdminQuotaConfigDTO,
  AdminQuotaResetDTO,
  AdminUserQuotaDetailDTO,
  PaginatedQuotaEventsDTO,
  PaginatedQuotaPeriodsDTO,
  QuotaMeStatusDTO,
  QuotaStatusDTO,
} from '@shared/schemas/quota';
import {
  adminUserQuotaDetailSchema,
  paginatedQuotaEventsSchema,
  paginatedQuotaPeriodsSchema,
  quotaMeStatusSchema,
  quotaStatusSchema,
} from '@shared/schemas/quota';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

function createApiUrl(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const url = new URL(path, apiBaseUrl);

  if (!query) {
    return url;
  }

  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    url.searchParams.set(key, String(value));
  });

  return url;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as ({
    error?: {
      code?: string;
      details?: Record<string, unknown>;
      message?: string;
    };
  } & T) | null;

  if (!response.ok || payload === null) {
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? response.statusText ?? 'La requête a échoué.',
      status: response.status,
    });
  }

  return payload as T;
}

async function getQuota<T>(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const response = await fetch(createApiUrl(path, query), {
    credentials: 'include',
    method: 'GET',
  });

  return parseJsonResponse<T>(response);
}

async function sendQuota<T>(
  method: 'POST' | 'PUT',
  path: string,
  body?: Record<string, unknown>,
) {
  const response = await fetch(createApiUrl(path), {
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    method,
  });

  return parseJsonResponse<T>(response);
}

export function getAdminUserQuotaDetail(userId: string) {
  return getQuota<AdminUserQuotaDetailDTO>(`/api/admin/users/${userId}/quota`).then((data) =>
    adminUserQuotaDetailSchema.parse(data),
  );
}

export function getCurrentQuotaStatus() {
  return getQuota<QuotaMeStatusDTO>('/api/quota/me').then((data) =>
    quotaMeStatusSchema.parse(data),
  );
}

export function configureAdminUserQuota(userId: string, body: AdminQuotaConfigDTO) {
  return sendQuota<QuotaStatusDTO>('PUT', `/api/admin/users/${userId}/quota`, body).then((data) =>
    quotaStatusSchema.parse(data),
  );
}

export function addAdminQuotaExtra(userId: string, body: AdminQuotaAddExtraDTO) {
  return sendQuota<QuotaStatusDTO>('POST', `/api/admin/users/${userId}/quota/extra`, body).then((data) =>
    quotaStatusSchema.parse(data),
  );
}

export function resetAdminUserQuota(userId: string, body: AdminQuotaResetDTO) {
  return sendQuota<QuotaStatusDTO>('POST', `/api/admin/users/${userId}/quota/reset`, body).then((data) =>
    quotaStatusSchema.parse(data),
  );
}

export function recomputeAdminUserQuota(userId: string) {
  return sendQuota<QuotaStatusDTO>('POST', `/api/admin/users/${userId}/quota/recompute`).then((data) =>
    quotaStatusSchema.parse(data),
  );
}

export function changeAdminUserAccountType(userId: string, body: AdminAccountTypeChangeDTO) {
  return sendQuota<QuotaStatusDTO>('POST', `/api/admin/users/${userId}/account-type`, body).then((data) =>
    quotaStatusSchema.parse(data),
  );
}

export function listAdminQuotaEvents(
  userId: string,
  query: { page: number; pageSize: number },
) {
  return getQuota<PaginatedQuotaEventsDTO>(`/api/admin/users/${userId}/quota/events`, query).then((data) =>
    paginatedQuotaEventsSchema.parse(data),
  );
}

export function listAdminQuotaPeriods(
  userId: string,
  query: { page: number; pageSize: number },
) {
  return getQuota<PaginatedQuotaPeriodsDTO>(`/api/admin/users/${userId}/quota/periods`, query).then((data) =>
    paginatedQuotaPeriodsSchema.parse(data),
  );
}
